import React from 'react';
import {Modal, TextContainer, TextStyle, Link, Form} from '@shopify/polaris';
import {WithI18nProps, withI18n} from '@shopify/react-i18n';
import {Routes, sendGoogleAnalytics, Payload} from '../../utilities';
import {ValidateStoreName} from 'components/polaris/react/validate-store-name';
import {Paragraph} from 'sections/services-marketplace/experts/components/typography';
import {producePageViewMonorailEvent} from '../../utilities/monorail';
import {
  MonorailPageIndicators,
  GoogleAnalyticsEventActions,
  GoogleAnalyticsEventType,
  GoogleAnalyticsEventCategories,
} from '../../utilities/constants';

export enum LoginModalContext {
  Login = 'login',
  CreateJob = 'createJob',
  RequestQuote = 'requestQuote',
}

interface OwnProps {
  open: boolean;
  modalText?: string;
  context?: LoginModalContext;
  expertHandle?: string;
  serviceHandle?: string;
  captionLink?: React.ReactNode;
  onClose(): void;
}

type CombinedProps = OwnProps & WithI18nProps;

interface State {
  shop: string;
  submitEnabled: boolean;
  customErrorMessage?: string;
}

class LoginModal extends React.PureComponent<CombinedProps, State> {
  state: State = {
    shop: '',
    submitEnabled: true,
    customErrorMessage: undefined,
  };

  onValidateStore = (shop: string, valid: boolean) => {
    this.setState({
      shop,
      submitEnabled: shop ? valid : true,
      customErrorMessage: undefined,
    });
  };

  onSubmit = () => {
    const {shop} = this.state;
    const {i18n} = this.props;

    if (!shop) {
      this.setState({
        customErrorMessage: i18n.translate('Global.PostAJob.emptyStateError'),
      });
      return;
    }

    this.sendEvents();

    window.location.assign(this.generateRedirectLocation());
  };

  sendEvents = () => {
    const {context, serviceHandle, expertHandle} = this.props;
    const payload: Payload = {
      eventCategory: GoogleAnalyticsEventCategories.user,
    };

    switch (context) {
      case LoginModalContext.CreateJob:
        payload.eventAction = GoogleAnalyticsEventActions.createAJob;
        producePageViewMonorailEvent({
          pageIndicator: MonorailPageIndicators.createAJob,
        });
        break;

      case LoginModalContext.RequestQuote:
        payload.eventAction = GoogleAnalyticsEventActions.requestQuote;
        payload.eventLabel = `${expertHandle}-${serviceHandle}`;
        producePageViewMonorailEvent({
          pageIndicator: MonorailPageIndicators.requestAQuote,
          serviceHandle,
        });
        break;

      default:
        payload.eventAction = GoogleAnalyticsEventActions.login;
        producePageViewMonorailEvent({
          pageIndicator: MonorailPageIndicators.login,
        });
    }

    sendGoogleAnalytics({type: GoogleAnalyticsEventType.Event, payload});

    return payload;
  };

  generateRedirectLocation = () => {
    const {context, expertHandle, serviceHandle} = this.props;
    const {shop} = this.state;
    let redirectLocation;

    switch (context) {
      case LoginModalContext.CreateJob:
        redirectLocation = Routes.loginPath(
          shop,
          Routes.postJobLoginPath({shop})
        );
        break;

      case LoginModalContext.RequestQuote:
        redirectLocation =
          expertHandle && serviceHandle
            ? Routes.loginPath(
                shop,
                Routes.postJobLoginPath({shop, expertHandle, serviceHandle})
              )
            : Routes.loginPath(shop, Routes.postJobLoginPath({shop}));
        break;

      default:
        redirectLocation = Routes.loginPath(shop);
    }

    return redirectLocation;
  };

  render() {
    const {submitEnabled, customErrorMessage} = this.state;
    const {open, onClose, modalText, captionLink, i18n} = this.props;

    const SignupLink = (
      <Link url={Routes.referralUrl('modal')} external>
        {i18n.translate('Global.PostAJob.domain')}
      </Link>
    );

    return (
      <Modal
        open={open}
        onClose={onClose}
        title={i18n.translate('Global.PostAJob.modalTitle')}
        primaryAction={{
          content: i18n.translate('Global.PostAJob.buttonText'),
          onAction: this.onSubmit,
          disabled: !submitEnabled,
        }}
        secondaryActions={[
          {
            content: i18n.translate('Global.PostAJob.closeModal'),
            onAction: onClose,
          },
        ]}
      >
        <Modal.Section>
          <Form onSubmit={this.onSubmit}>
            <TextContainer>
              {modalText && <Paragraph>{modalText}</Paragraph>}
              <ValidateStoreName
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onValidateStore={this.onValidateStore}
                customErrorMessage={customErrorMessage}
              />
              <p>
                <TextStyle variation="subdued">
                  {captionLink
                    ? captionLink
                    : i18n.translate('Global.PostAJob.signupText', {
                        link: SignupLink,
                      })}
                </TextStyle>
              </p>
            </TextContainer>
          </Form>
        </Modal.Section>
      </Modal>
    );
  }
}

export default withI18n()(LoginModal);
